import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  FormControlLabel,
  Switch,
  Chip,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import api from "../../services/api";
import { toast } from "react-hot-toast";

const getTranslatedText = (translationObj, defaultLang = "ja") => {
  if (!translationObj) return "";

  if (typeof translationObj !== "object") return translationObj;

  return (
    translationObj[defaultLang] ||
    translationObj.ja ||
    translationObj.en ||
    translationObj.zh ||
    ""
  );
};

const MenuManagement = () => {
  const { storeId } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    categoryId: "",
    unit: "",
    available: true,
    allergens: [],
    options: [],
    calories: 0,
    displayOrder: 0,
    imageUrl: "",
    stockManagement: {
      enabled: false,
      currentStock: 0,
    },
  });
  const [categoryFormData, setCategoryFormData] = useState({
    name: "",
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("メニューデータを取得中...");
        console.log("Store ID:", storeId);
      }

      const [productsRes, categoriesRes] = await Promise.all([
        api.get(`/api/menu/${storeId}/products`),
        api.get(`/api/menu/${storeId}/categories`),
      ]);

      console.log("Products response:", productsRes.data);
      console.log("Categories response:", categoriesRes.data);

      // カテゴリーごとに商品をグループ化
      // Note: APIレスポンスインターセプターがsnake_caseをcamelCaseに変換するため、両方チェック
      const groupedProducts = categoriesRes.data.map((category) => ({
        categoryId: category._id || category.id,
        categoryName: getTranslatedText(category.name),
        products: productsRes.data.filter(
          (product) => {
            const productCategoryId = product.categoryId || product.category_id || product.category?._id || product.category?.id || product.category;
            const categoryId = category._id || category.id;
            return productCategoryId === categoryId;
          }
        ),
      }));

      setProducts(groupedProducts);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error("データの取得に失敗しました:", error);
      setError(error.response?.data?.message || "データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        name: product.name,
        description: product.description || "",
        price: product.price,
        categoryId: product.categoryId || product.category_id || product.category?._id || product.category?.id || product.category || "",
        unit: product.unit || "",
        available: product.available !== false,
        allergens: product.allergens || [],
        options: product.options || [],
        calories: product.calories || 0,
        displayOrder: product.displayOrder || 0,
        imageUrl: product.imageUrl || product.image_url || "",
        stockManagement: {
          enabled: product.stockManagement?.enabled || false,
          currentStock: product.stockManagement?.currentStock || 0,
        },
      });
    } else {
      setSelectedProduct(null);
      setFormData({
        name: "",
        description: "",
        price: "",
        categoryId: "",
        unit: "",
        available: true,
        allergens: [],
        options: [],
        calories: 0,
        displayOrder: 0,
        imageUrl: "",
        stockManagement: {
          enabled: false,
          currentStock: 0,
        },
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null);
    setFormData({
      name: "",
      description: "",
      price: "",
      categoryId: "",
      unit: "",
      available: true,
      allergens: [],
      options: [],
      calories: 0,
      displayOrder: 0,
      imageUrl: "",
      stockManagement: {
        enabled: false,
        currentStock: 0,
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const productData = {
        ...formData,
        stockManagement: {
          enabled: formData.stockManagement?.enabled || false,
          currentStock: formData.stockManagement?.currentStock || 0,
        },
      };

      if (selectedProduct) {
        const productId = selectedProduct._id || selectedProduct.id;
        // 商品情報を更新
        await api.put(
          `/api/menu/${storeId}/products/${productId}`,
          productData
        );

        // 在庫管理が有効な場合、在庫数を更新
        if (productData.stockManagement.enabled) {
          await api.patch(
            `/api/menu/${storeId}/products/${productId}/stock`,
            {
              quantity: productData.stockManagement.currentStock,
            }
          );
        }

        // 販売状態を更新
        await api.patch(
          `/api/menu/${storeId}/products/${productId}/availability`,
          {
            available: productData.available,
          }
        );

        toast.success("商品情報を更新しました");
      } else {
        // 新規商品の作成
        await api.post(
          `/api/menu/${storeId}/products`,
          productData
        );
        toast.success("商品を追加しました");
      }

      handleCloseDialog();
      await fetchData(); // データを再取得
    } catch (error) {
      console.error("商品の保存に失敗しました:", error);
      toast.error(error.response?.data?.message || "商品の保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm("この商品を削除してもよろしいですか？")) {
      try {
        setDeletingId(productId);
        await api.delete(`/api/menu/${storeId}/products/${productId}`);
        fetchData();
      } catch (error) {
        console.error("商品の削除に失敗しました:", error);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleOpenCategoryDialog = () => {
    setOpenCategoryDialog(true);
  };

  const handleCloseCategoryDialog = () => {
    setOpenCategoryDialog(false);
    setCategoryFormData({ name: "" });
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/menu/${storeId}/categories`, categoryFormData);
      handleCloseCategoryDialog();
      fetchData();
    } catch (error) {
      console.error("カテゴリーの保存に失敗しました:", error);
    }
  };

  const handleImageUpload = async (productId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await api.post(
        `/api/menu/${storeId}/products/${productId}/image`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("画像アップロード成功:", response.data);
      fetchData();
    } catch (error) {
      console.error("画像アップロードエラー:", error);
      setError("画像のアップロードに失敗しました");
    }
  };

  // 在庫数更新処理（将来使用予定）
  // eslint-disable-next-line no-unused-vars
  const handleStockUpdate = async (productId, newQuantity) => {
    try {
      setLoading(true);
      const response = await api.patch(
        `/api/menu/${storeId}/products/${productId}/stock`,
        { quantity: newQuantity }
      );

      // 更新された商品情報で状態を更新
      const updatedProducts = products.map((category) => ({
        ...category,
        products: category.products.map((product) =>
          (product._id || product.id) === productId ? response.data.product : product
        ),
      }));
      setProducts(updatedProducts);

      toast.success("在庫数を更新しました");
    } catch (error) {
      console.error("在庫更新エラー:", error);
      toast.error(error.response?.data?.message || "在庫の更新に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  // 販売状態更新処理（将来使用予定）
  // eslint-disable-next-line no-unused-vars
  const handleAvailabilityToggle = async (productId, newAvailable) => {
    try {
      setLoading(true);
      const response = await api.patch(
        `/api/menu/${storeId}/products/${productId}/availability`,
        { available: newAvailable }
      );

      // 更新された商品情報で状態を更新
      const updatedProducts = products.map((category) => ({
        ...category,
        products: category.products.map((product) =>
          (product._id || product.id) === productId ? response.data.product : product
        ),
      }));
      setProducts(updatedProducts);

      toast.success("販売状態を更新しました");
    } catch (error) {
      console.error("販売状態更新エラー:", error);
      toast.error(
        error.response?.data?.message || "販売状態の更新に失敗しました"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3} aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Typography>読み込み中...</Typography>
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h5">メニュー管理</Typography>
            <Box>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenCategoryDialog}
                disabled={loading || saving}
                sx={{ mr: 2 }}
              >
                カテゴリーを追加
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleOpenDialog()}
                disabled={loading || saving}
              >
                商品を追加
              </Button>
            </Box>
          </Box>

          {products.length === 0 ? (
            <Typography>メニューアイテムがありません</Typography>
          ) : (
            <Grid container spacing={3}>
              {products.map((category) => (
                <Grid item xs={12} key={category.categoryId}>
                  <Paper elevation={3}>
                    <Box p={2}>
                      <Typography variant="h6" gutterBottom>
                        {category.categoryName}
                      </Typography>
                      <Grid container spacing={2}>
                        {category.products.map((product) => (
                          <Grid item xs={12} sm={6} md={4} key={product._id || product.id}>
                            <Paper elevation={2}>
                              <Box p={2}>
                                {(product.imageUrl || product.image_url) ? (
                                  <Box
                                    sx={{
                                      position: "relative",
                                      width: "100%",
                                      paddingTop: "75%",
                                      mb: 2,
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={
                                        (product.imageUrl || product.image_url).startsWith('http')
                                          ? (product.imageUrl || product.image_url)
                                          : `${api.defaults.baseURL}${product.imageUrl || product.image_url}`
                                      }
                                      alt={getTranslatedText(product.name)}
                                      sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: 200,
                                      backgroundColor: "#f5f5f5",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Typography color="textSecondary">
                                      画像なし
                                    </Typography>
                                  </Box>
                                )}
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  id={`image-upload-${product._id || product.id}`}
                                  onChange={(e) =>
                                    handleImageUpload(product._id || product.id, e)
                                  }
                                />
                                <label htmlFor={`image-upload-${product._id || product.id}`}>
                                  <IconButton
                                    component="span"
                                    color="primary"
                                    sx={{
                                      position: "absolute",
                                      top: 8,
                                      right: 8,
                                    }}
                                  >
                                    <AddPhotoAlternateIcon />
                                  </IconButton>
                                </label>
                                <Typography variant="subtitle1">
                                  {getTranslatedText(product.name)}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {getTranslatedText(product.description)}
                                </Typography>
                                <Typography variant="h6">
                                  ¥{product.price.toLocaleString()}
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Chip
                                    label={
                                      product.available ? "販売中" : "売切"
                                    }
                                    color={
                                      product.available ? "success" : "error"
                                    }
                                    size="small"
                                  />
                                  <Box sx={{ flexGrow: 1 }} />
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenDialog(product)}
                                    sx={{ mr: 1 }}
                                    disabled={Boolean(deletingId)}
                                  >
                                    編集
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleDelete(product._id || product.id)}
                                    disabled={deletingId === (product._id || product.id)}
                                  >
                                    {deletingId === (product._id || product.id) ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      "削除"
                                    )}
                                  </Button>
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedProduct ? "商品を編集" : "新規商品を追加"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ mb: 2, textAlign: "center" }}>
                  {(selectedProduct?.imageUrl || selectedProduct?.image_url) ? (
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        maxWidth: 300,
                        margin: "0 auto",
                        paddingTop: "75%",
                      }}
                    >
                      <Box
                        component="img"
                        src={
                          (selectedProduct.imageUrl || selectedProduct.image_url).startsWith('http')
                            ? (selectedProduct.imageUrl || selectedProduct.image_url)
                            : `${api.defaults.baseURL}${selectedProduct.imageUrl || selectedProduct.image_url}`
                        }
                        alt={getTranslatedText(selectedProduct.name)}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 300,
                        height: 200,
                        margin: "0 auto",
                        backgroundColor: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 1,
                      }}
                    >
                      <Typography color="textSecondary">画像なし</Typography>
                    </Box>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    id="dialog-image-upload"
                    onChange={(e) =>
                      selectedProduct &&
                      handleImageUpload(selectedProduct._id || selectedProduct.id, e)
                    }
                  />
                  <label htmlFor="dialog-image-upload">
                    <Button
                      component="span"
                      startIcon={<AddPhotoAlternateIcon />}
                      sx={{ mt: 2 }}
                    >
                      画像をアップロード
                    </Button>
                  </label>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="商品名"
                  name="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="説明"
                  name="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="価格（税込）"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  required
                  InputProps={{
                    endAdornment: <Typography sx={{ ml: 1 }}>円</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>カテゴリー</InputLabel>
                  <Select
                    value={formData.categoryId}
                    onChange={(e) =>
                      setFormData({ ...formData, categoryId: e.target.value })
                    }
                    label="カテゴリー"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category._id || category.id} value={category._id || category.id}>
                        {getTranslatedText(category.name)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.available}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          available: e.target.checked,
                        })
                      }
                      color="primary"
                    />
                  }
                  label={formData.available ? "販売中" : "売切"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="画像URL"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, imageUrl: e.target.value })
                  }
                  placeholder="https://example.com/image.jpg"
                  helperText="外部の画像URLを入力してください"
                />
                {formData.imageUrl && (
                  <Box sx={{ mt: 1, textAlign: "center" }}>
                    <Box
                      component="img"
                      src={formData.imageUrl}
                      alt="プレビュー"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 150,
                        objectFit: "contain",
                        borderRadius: 1,
                        border: "1px solid #ddd",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                  アレルギー品目（クリックで選択/解除）
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  特定原材料7品目（表示義務）
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                  {["卵", "乳", "小麦", "そば", "落花生", "えび", "かに"].map((allergen) => (
                    <Chip
                      key={allergen}
                      label={allergen}
                      onClick={() => {
                        const newAllergens = formData.allergens.includes(allergen)
                          ? formData.allergens.filter((a) => a !== allergen)
                          : [...formData.allergens, allergen];
                        setFormData({ ...formData, allergens: newAllergens });
                      }}
                      color={formData.allergens.includes(allergen) ? "error" : "default"}
                      variant={formData.allergens.includes(allergen) ? "filled" : "outlined"}
                      sx={{ cursor: "pointer" }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  特定原材料に準ずるもの（表示推奨）
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {[
                    "アーモンド", "あわび", "いか", "いくら", "オレンジ",
                    "カシューナッツ", "キウイフルーツ", "牛肉", "くるみ", "ごま",
                    "さけ", "さば", "大豆", "鶏肉", "バナナ",
                    "豚肉", "まつたけ", "もも", "やまいも", "りんご", "ゼラチン"
                  ].map((allergen) => (
                    <Chip
                      key={allergen}
                      label={allergen}
                      onClick={() => {
                        const newAllergens = formData.allergens.includes(allergen)
                          ? formData.allergens.filter((a) => a !== allergen)
                          : [...formData.allergens, allergen];
                        setFormData({ ...formData, allergens: newAllergens });
                      }}
                      color={formData.allergens.includes(allergen) ? "warning" : "default"}
                      variant={formData.allergens.includes(allergen) ? "filled" : "outlined"}
                      sx={{ cursor: "pointer" }}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="カロリー"
                  type="number"
                  value={formData.calories}
                  onChange={(e) =>
                    setFormData({ ...formData, calories: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="表示順"
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) =>
                    setFormData({ ...formData, displayOrder: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  オプション
                </Typography>
                {formData.options.map((option, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #ddd",
                      borderRadius: 1,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="オプション名"
                          value={option.name}
                          onChange={(e) => {
                            const newOptions = [...formData.options];
                            newOptions[index].name = e.target.value;
                            setFormData({ ...formData, options: newOptions });
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="価格"
                          type="number"
                          value={option.price}
                          onChange={(e) => {
                            const newOptions = [...formData.options];
                            newOptions[index].price = e.target.value;
                            setFormData({ ...formData, options: newOptions });
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={option.isRequired}
                              onChange={(e) => {
                                const newOptions = [...formData.options];
                                newOptions[index].isRequired = e.target.checked;
                                setFormData({
                                  ...formData,
                                  options: newOptions,
                                });
                              }}
                            />
                          }
                          label="必須"
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        color="error"
                        onClick={() => {
                          const newOptions = formData.options.filter(
                            (_, i) => i !== index
                          );
                          setFormData({ ...formData, options: newOptions });
                        }}
                      >
                        削除
                      </Button>
                    </Box>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      options: [
                        ...formData.options,
                        {
                          name: "",
                          price: 0,
                          description: "",
                          isRequired: false,
                        },
                      ],
                    });
                  }}
                >
                  オプションを追加
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : "保存"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={openCategoryDialog}
        onClose={handleCloseCategoryDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>カテゴリーを追加</DialogTitle>
        <form onSubmit={handleCategorySubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="カテゴリー名"
              value={categoryFormData.name}
              onChange={(e) =>
                setCategoryFormData({
                  ...categoryFormData,
                  name: e.target.value,
                })
              }
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCategoryDialog}>キャンセル</Button>
            <Button type="submit" variant="contained" color="primary">
              保存
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default MenuManagement;
